<template>
  <div>
    <DashShellComponent
      :has-data="!!selectedInvoice"
      :tabs="invoiceTabs"
    >
      <!-- Slot for Selection Pane -->
      <template #selection-pane>
        <SelectionPane 
          :fileFormatter="formatInvoices" 
          @selected="handleSelection" 
          @add-to-ledger="handleNewEntry" 
          @context-changed="getSelectedContext"
        />
      </template>

      <!-- Slot for Header -->
      <template #header-content>
        <HeaderAndContent 
          :selectedInvoice="selectedInvoice" 
          :selectedClient="selectedClient" 
          :selectedQuote="selectedQuote" 
        />
      </template>
    </DashShellComponent>
  </div>
</template>

<script>
import axios from "@/plugins/axios"
import helpersJs from "@/plugins/helpers"
import companiesJs from "@/plugins/companies"
import invoicesAndQuotesJs from "@/plugins/invoicesAndQuotes";
import AutoCompleteCrudAdvanced from "@/modules/CrudDatabases/AutoCompleteCrudAdvanced.vue"
import BillingItemsTable from "@/modules/CaseManager/BillingItemsTable.vue";
import BillingEntryDialog from '@/modules/CaseManager/BillingEntryDialog.vue';
import BillingTimer from '@/modules/CaseManager/BillingTimer.vue';
import ClientManager from "@/modules/Clients/ClientManager.vue"
import CaseDocumentsManager from "@/modules/CaseManager/CaseDocumentsManager.vue";
import CaseChronology from "@/modules/CaseManager/CaseChronology.vue";
import TrustAccountLedger from "@/modules/TrustAccount/TrustAccountLedger.vue"
import Quotations from "@/modules/InvoicesAndQuotes/Quotations.vue";
import WelcomeNotice from "@/components/General/WelcomeNotice.vue"
import InvoicePageHeader from "@/modules/InvoicesAndQuotes/InvoicePageHeader.vue"
import SelectionPane from "@/components/General/SelectionPane.vue";
import HeaderAndContent from "@/components/General/HeaderAndContent.vue"
import DashShellComponent from "@/components/General/DashShellComponent.vue";

export default {
    name:"GeneralIndustryDashboard",
    components:{
    AutoCompleteCrudAdvanced,
    BillingEntryDialog,
    BillingTimer,
    ClientManager,
    TrustAccountLedger,
    CaseDocumentsManager,
    CaseChronology,
    BillingItemsTable,
    Quotations,
    WelcomeNotice,
    InvoicePageHeader,
    SelectionPane,
    HeaderAndContent,
    DashShellComponent
},
    computed:{
        isMobile(){
            return this.$store.getters.isMobile
        },
        getThemeColor(){
            return this.$store.getters.getThemeColor
        },
        getAuthenticated(){
            return this.$store.getters.getAuthenticated
        },
        getAccessToken(){
            return this.$store.getters.getAccessToken
        },
        getTertiaryColor(){
            return this.$store.getters.getTertiaryColor
        },
        getFileLabel(){
          return this.$store.getters.getFileLabel
        },
        getTrustAccLabel(){
            return this.$store.getters.getTrustAccLabel
        },
        getCurrency(){
            return this.$store.getters.getCurrency
        },
        gradientIconStyle() {
          return this.$store.getters.gradientIconStyle
        },
        totalBillable() {
            let total = 0;
            if(!this.selectedInvoice || !this.selectedInvoice.billingEntries || this.selectedInvoice.billingEntries.length < 1){
                return total;
            }

            for (var i = 0; i < this.selectedInvoice.billingEntries.length; i++) {
              var entry = this.selectedInvoice.billingEntries[i];
              total += Number(entry.price) + this.calculateVAT(entry);
            }
            
            return total;
          },
          invoiceTabs() {
            if (!this.selectedInvoice) return [];

            return [
              {
                label: '3. Billing & Expenses',
                mobileLabel: '3. Billing',
                icon: 'mdi-cash-register',
                component: BillingItemsTable,
                props: {
                  selectedInvoice: this.selectedInvoice,
                  itemLabel: 'Invoice',
                  referenceNumber: 'invoice_number'
                },
                on: {
                  'billing-added': this.refreshBillingEntries
                }
              },
              {
                label: `4. ${this.getTrustAccLabel} Account`,
                mobileLabel: '4. Account',
                icon: 'mdi-bank-outline',
                component: TrustAccountLedger,
                props: {
                  scope: 'case',
                  reference: this.selectedInvoice.invoice_number
                }
              },
              {
                label: '5. Activities & Tasks',
                mobileLabel: '5. Tasks',
                icon: 'mdi-format-list-checks',
                component: CaseChronology,
                props: {
                  caseData: this.selectedInvoice
                }
              },
              {
                label: '6. Documents',
                mobileLabel: '6. Docs',
                icon: 'mdi-file-document-multiple',
                component: CaseDocumentsManager,
                props: {
                  selectedCase: this.selectedInvoice,
                  emis: this.selectedInvoice.emis
                },
                on: {
                  'refresh-documents': this.refreshDocs
                }
              }
            ];
          }
        },
    data:()=>({
        quotations:[],
        selectedContext: null,
        activeTab:null,
        principalItemLabel:"Invoices",
        selectedInvoice: null,
        selectedQuote:null,
        selectedClient: null,
        showBillingDialog:false,
        deleteBillingEntryDialog:false,
        deleteBillingEntryItem:null,
        billingSearch: '',
        ledgerFilter: 'All',
        billingHeaders: [
            {text:"Del",value:"id"},
          
          { text: 'Description', value: 'name' },
          { text: 'Amount (Excl)', value: 'price' },
          { text: 'VAT (15%)', value: 'tax_amount', sortable: false },
          { text: 'Total', value: 'lineTotal', sortable: false }
        ]
    }),
    async mounted(){
      this.initialiseComponent()
    },
    methods:{
        async initialiseComponent(){
          if(!this.getAuthenticated){
            return;
          }
          const userCompany = await companiesJs.getUserCompany()
          helpersJs.initialiseOrUpdateCompanyDataInStorage(null, userCompany)
        },
        //destructuring the response to get the invoices only and not status
        formatInvoices({invoices}){
             if(!invoices || !invoices.length) return []
            const formattedInvoices = [];
            invoices.forEach(invoice => {
                formattedInvoices.push({
                    ...invoice,
                    name: `${invoice.client?.name} | ${invoice.invoice_number}`
                })
            });
            return formattedInvoices;
        },
        async handleSelection(selection){
          if(!selection) return;
          const category = selection.category
          const selectedItem = selection.data
          this.selectedContext = true;

            if(category === 'primary'){
              await this.getSelectedInvoice(selectedItem)
            }else if(category === 'client'){
              this.getSelectedClient(selectedItem)
            }else{ //its a quote
              this.getSelectedQuote(selectedItem)
            }
        },
        async getSelectedInvoice(invoice){
            this.selectedClient = null;
            this.selectedQuote = null
            this.selectedInvoice = invoice;
            this.$store.commit("setLoader",true)
            const res = await invoicesAndQuotesJs.getSelectedInvoice(invoice.invoice_number)
            this.$store.commit("setLoader",false)
            if(res && res.status === 'success'){
              this.selectedInvoice = res.invoice
            }else{
              this.$toast.error("Oops, Something went wrong!",{duration:10000})
            }
        },
        getSelectedClient(selectedClient){
            this.selectedInvoice = null
            this.selectedQuote = null
            this.selectedClient = selectedClient
        },
        getSelectedContext(){
          this.selectedInvoice = null
          this.selectedClient = null
          this.selectedQuote = null
        },
        //done
      getAndSetConvertedInvoice(convertedInvoice){
        this.selectedClient = null;
        this.selectedQuote = null;
        this.selectedInvoice = convertedInvoice;
      },
        formatQuotes({quotes}){
            if(!quotes || !quotes.length) return []
            const quotations = [];
            
            quotes.forEach(quote => {
                quotations.push({
                        ...quote,
                        name: `${quote.client?.name} | ${quote.quote_number}`
                    })
            });
            this.quotations = quotations
            return quotations;
        },
        getSelectedQuote(quote){
          this.selectedClient = null
          this.selectedInvoice = null
            this.selectedQuote = quote
        },
        //done
        filterByType: function(value, search, item) {
          // 1. Handle Type Filter
          if (this.ledgerFilter !== 'All' && item.type !== this.ledgerFilter) {
            return false;
          }
          
          // 2. Handle Text Search (Case Insensitive)
          if (search) {
            var description = item.description.toLowerCase();
            var amount = item.amount.toString();
            return description.indexOf(search.toLowerCase()) !== -1 || amount.indexOf(search) !== -1;
          }

          return true;
        },
         //done
        openDeleteBillingEntryDialog(item){
          this.deleteBillingEntryItem = item
          this.deleteBillingEntryDialog = true
        },
        //done
        addBillingEntry() {
            this.showBillingDialog = true;
        },
        //done
        formatCurrency(value) {
          if(!value || value === 0){
              return this.getCurrency + '0';
          }
          return this.getCurrency + helpersJs.formatMoney(value);
        },
        //done
        calculateVAT(item) {
          if (item.isVatable) {
            return Number(item.price) * 0.15;
          }
          return 0;
        },
        //done
        refreshDocs(docs){
          this.selectedInvoice.documents = docs
        },
        //done
        refreshBillingEntries(billingEntries) {
          this.selectedInvoice.billingEntries = billingEntries
        },
        handleNewEntry(newEntry) {
        this.selectedInvoice.billingEntries.push(newEntry);
        
        this.$store.commit('setLoader',true)

        axios.postDataWithToken("/auth/save-or-update-case-billings",{
          internalRef:this.selectedInvoice.invoice_number,
          invoiceNo: this.selectedInvoice.invoice_number,
          billingEntries: this.selectedInvoice.billingEntries
        },
        this.getAccessToken)
        .then(res => {
          this.$store.commit('setLoader',false)
          if(res && res.status == "success"){
            this.$toast.success("Billing entry added successfuly!",{duration:10000})
            this.selectedInvoice.billingEntries = res.billingEntries
          }else{
            this.$toast.error('Oops, something went wrong!',{duration:10000})
          }
        })
  },
    }
}
</script>

<style scoped>
/* Prominent Mobile Bottom Navigation Customization */
.mobile-nav-bar {
  border-top: 2px solid rgba(0, 0, 0, 0.08);
}

.custom-nav-btn {
  height: 100% !important;
  padding: 6px 0 !important;
}

/* Forces labels to be permanently visible with large typography */
.custom-nav-btn .nav-label {
  display: block !important;
  font-size: 0.85rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em;
  margin-top: 2px;
  opacity: 0.85;
}

/* Styling for the active state tab */
.custom-nav-btn.v-btn--active .nav-label {
  opacity: 1;
  font-size: 0.9rem !important;
}

.custom-nav-btn.v-btn--active .v-icon {
  transform: scale(1.15);
  transition: transform 0.2s ease;
}
</style>